// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { forwardRef, useEffect, useRef, useState } from "react"
import { useLocation, Link } from "react-router-dom"
import type { ProfileResponse } from "@bitebalance/contracts"
import type { GlossaryTermId } from "@bitebalance/i18n"
import { useApiClient } from "@/api/useApiClient"
import { useI18n, formatEurFromCents } from "@/i18n"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { InfoButton } from "@/components/ui/InfoButton"
import { centsToInputString, parseEurInputToCents } from "@/lib/budgetAmount"
import {
  BUDGET_SECTION_ID,
  initialSetupForm,
  migrationState,
  migrationTransition,
  readCollapsedFlag,
  setupValidation,
  writeCollapsedFlag,
  type MigrationEvent,
  type SetupForm,
} from "@/lib/budgetMigration"
import { glossaryTerm } from "@/lib/glossary"
import { BUDGET_GUIDE_PATH } from "@/lib/budgetGuide"
import { BudgetOutlook } from "./BudgetOutlook"

type Budget = ProfileResponse["budget"]

/**
 * "GROCERY BUDGET" in Settings (Budget Model v2, ADR-052 §11.2) — the one
 * place that defines and manages the two independent optional amounts:
 *
 *   Weekly spending goal ⓘ   €___ / week   [Remove]   (SOFT — an average)
 *   Maximum checkout ⓘ  Optional  €___     [Remove]   (HARD — no override)
 *
 * Rules: German decimal comma accepted; a parse error is SHOWN and never
 * silently dropped; explicit Remove per field; empty states; the PATCH
 * always carries the v2 pair (both fields, the unchanged one carried) and
 * NEVER the legacy pair from this page; a budget change does not rebuild
 * the plan.
 *
 * Pre-v2 accounts (`budget.model === "legacy_hard_cap"`, redesign
 * 2026-09-04): the old amount keeps capping every shop until the account is
 * migrated, and the ONLY migration path is the SETUP card's Save — one
 * PATCH with the v2 pair the user configured directly (goal prefilled with
 * the previous amount and editable to any amount, optional single-shop
 * maximum, "no goal" for maximum-only). "Decide later" collapses the card
 * into a persistent pending notice (per-device flag, see budgetMigration.ts)
 * — never a PATCH, and never a state where the unresolved amount is hidden.
 * The "previous budget" row (maximum-checkout ⓘ) stays visible while
 * unclassified. No internal wording ("legacy", "hard") ever reaches the UI.
 */
export function GroceryBudgetSection({
  profile,
  onProfileUpdated,
}: {
  profile: ProfileResponse
  onProfileUpdated: (updated: ProfileResponse) => void
}) {
  const api = useApiClient()
  const { t, locale } = useI18n()
  const { hash } = useLocation()
  const budget: Budget = profile.budget
  const legacy = budget.model === "legacy_hard_cap"

  const [goalText, setGoalText] = useState(() => centsToInputString(budget.weeklyGoalEurCents, locale))
  const [maxText, setMaxText] = useState(() => centsToInputString(budget.maxCheckoutEurCents, locale))
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle")
  const [showErrors, setShowErrors] = useState(false)
  const [outlookRefresh, setOutlookRefresh] = useState(0)
  const goalInputRef = useRef<HTMLInputElement>(null)
  const sectionRef = useRef<HTMLElement>(null)

  // ---- Migration setup card (unclassified pre-v2 amount) -----------------
  const legacyAmount = budget.legacyWeeklyLimitEurCents
  const legacyAmountText = legacyAmount === null ? "" : formatEurFromCents(legacyAmount, locale)
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsedFlag())
  const [setupForm, setSetupForm] = useState<SetupForm>(() => initialSetupForm(legacyAmount, locale))
  const [setupSaveState, setSetupSaveState] = useState<"idle" | "saving" | "error">("idle")
  const [justMigrated, setJustMigrated] = useState(false)
  const [maxTouched, setMaxTouched] = useState(false)
  const [focusSetup, setFocusSetup] = useState(false)
  const setupGoalRef = useRef<HTMLInputElement>(null)
  const state = migrationState({ model: budget.model, collapsed })

  function dispatch(event: MigrationEvent) {
    const { next } = migrationTransition({ model: budget.model, collapsed }, event)
    if (next.collapsed !== collapsed) {
      setCollapsed(next.collapsed)
      writeCollapsedFlag(next.collapsed)
    }
  }

  // Re-sync the inputs when the server profile changes (e.g. after the
  // setup PATCH flips the model to v2).
  useEffect(() => {
    setGoalText(centsToInputString(budget.weeklyGoalEurCents, locale))
    setMaxText(centsToInputString(budget.maxCheckoutEurCents, locale))
    // locale is intentionally NOT a dependency: retyping the separator under
    // the user while they edit would be a surprise; the parser accepts both.
  }, [budget.weeklyGoalEurCents, budget.maxCheckoutEurCents, budget.model])

  // Server says v2 → the device flag is meaningless; clear it.
  useEffect(() => {
    if (!legacy && collapsed) {
      setCollapsed(false)
      writeCollapsedFlag(false)
    }
  }, [legacy, collapsed])

  // A CTA elsewhere (Today / Plan / the pending notice) lands here with
  // `#grocery-budget`: expand the setup card and bring it into view.
  useEffect(() => {
    if (hash !== `#${BUDGET_SECTION_ID}`) return
    if (legacy) {
      dispatch({ type: "SET_UP" })
      setFocusSetup(true)
    }
    sectionRef.current?.scrollIntoView?.()
    // The section mounts once the profile is loaded, so `legacy` is known
    // here; re-running on other changes would re-scroll under the user.
  }, [hash])

  useEffect(() => {
    if (focusSetup && state === "unclassified_expanded") {
      setupGoalRef.current?.focus()
      setFocusSetup(false)
    }
  }, [focusSetup, state])

  const parsedGoal = parseEurInputToCents(goalText)
  const parsedMax = parseEurInputToCents(maxText)
  const goalInvalid = parsedGoal.kind === "invalid"
  const maxInvalid = parsedMax.kind === "invalid"

  const goalTerm = glossaryTerm("weekly-spending-goal", locale)
  const maxTerm = glossaryTerm("maximum-checkout", locale)

  async function patchBudget(weeklyGoalEurCents: number | null, maxCheckoutEurCents: number | null) {
    // ONLY the v2 pair ever leaves this page (BudgetPatchSchema, strict).
    const updated = await api.patchProfile({ budget: { weeklyGoalEurCents, maxCheckoutEurCents } })
    onProfileUpdated(updated as unknown as ProfileResponse)
    setOutlookRefresh(n => n + 1)
    return updated
  }

  async function handleSave() {
    setShowErrors(true)
    if (goalInvalid || maxInvalid) return
    setSaveState("saving")
    try {
      await patchBudget(
        parsedGoal.kind === "cents" ? parsedGoal.cents : null,
        parsedMax.kind === "cents" ? parsedMax.cents : null
      )
      setSaveState("saved")
      setShowErrors(false)
    } catch {
      setSaveState("error")
    }
  }

  async function handleRemove(field: "goal" | "max") {
    // Remove clears the field and persists at once, carrying the OTHER
    // field's current server value unchanged.
    if (field === "goal") setGoalText("")
    else setMaxText("")
    setSaveState("saving")
    try {
      await patchBudget(
        field === "goal" ? null : budget.weeklyGoalEurCents,
        field === "max" ? null : budget.maxCheckoutEurCents
      )
      setSaveState("saved")
    } catch {
      setSaveState("error")
    }
  }

  const setupCheck = setupValidation(setupForm)

  async function handleSetupSave() {
    // SAVE is the ONE transition that produces a PATCH — the exact v2 pair.
    const { patch } = migrationTransition({ model: budget.model, collapsed }, { type: "SAVE", form: setupForm })
    if (!patch) return
    setSetupSaveState("saving")
    try {
      await patchBudget(patch.budget.weeklyGoalEurCents, patch.budget.maxCheckoutEurCents)
      // The server now answers model "v2": the card disappears via the
      // profile prop; the device flag is cleared; the confirmation shows.
      setCollapsed(false)
      writeCollapsedFlag(false)
      setSetupSaveState("idle")
      setJustMigrated(true)
    } catch {
      setSetupSaveState("error")
    }
  }

  function editSetup(patch: Partial<SetupForm>) {
    // EDIT never PATCHes — local form state only.
    setSetupForm(f => ({ ...f, ...patch }))
    setSetupSaveState(s => (s === "error" ? "idle" : s))
  }

  // Inline messages: garbage is flagged live; an EMPTY enabled amount is
  // flagged once the user has touched it (the goal is prefilled, so empty
  // is always the user's doing; the maximum starts empty on reveal).
  const setupGoalShowError = setupForm.goalEnabled && setupCheck.goalInvalid
  const setupMaxShowError =
    setupForm.maxEnabled &&
    setupCheck.maxInvalid &&
    (maxTouched || parseEurInputToCents(setupForm.maxText).kind === "invalid")

  return (
    <>
      <section
        id={BUDGET_SECTION_ID}
        ref={sectionRef}
        aria-labelledby="grocery-budget-heading"
        className="flex scroll-mt-24 flex-col gap-5 rounded-bb-md border border-line bg-paper p-5"
      >
        <h2 id="grocery-budget-heading" className="text-lg font-semibold text-ink">
          {t("views.budget.sectionTitle")}
        </h2>
        <p className="mt-1 text-sm text-ink-2">
          <Link
            to={BUDGET_GUIDE_PATH}
            className="font-semibold text-green underline underline-offset-2"
            data-testid="budget-guide-link"
          >
            {t("views.budget.budgetGuideLinkText")}
          </Link>
        </p>

        {state === "unclassified_expanded" && (
          <div
            role="region"
            aria-labelledby="budget-setup-card-title"
            data-testid="budget-setup-card"
            className="flex flex-col gap-4 rounded-bb-lg border border-gold/50 bg-gold/10 p-4"
          >
            <div className="flex flex-col gap-1">
              <p id="budget-setup-card-title" className="font-semibold text-ink">
                {t("views.budget.setupTitle")}
              </p>
              <p className="text-sm text-ink-2">{t("views.budget.setupBody", { amount: legacyAmountText })}</p>
            </div>

            <AmountField
              id="setup-goal"
              ref={setupGoalRef}
              label={goalTerm.name}
              termId="weekly-spending-goal"
              helper={t("views.budget.setupGoalHelper")}
              suffix={t("views.budget.perWeek")}
              value={setupForm.goalEnabled ? setupForm.goalText : ""}
              onChange={v => editSetup({ goalText: v })}
              disabled={!setupForm.goalEnabled}
              invalid={setupGoalShowError}
              errorText={t("views.budget.invalidAmount")}
            />

            <CheckboxRow
              id="setup-no-goal"
              label={t("views.budget.setupNoGoal")}
              checked={!setupForm.goalEnabled}
              onChange={checked => editSetup({ goalEnabled: !checked })}
            />

            <CheckboxRow
              id="setup-max-toggle"
              label={t("views.budget.setupMaxToggle")}
              checked={setupForm.maxEnabled}
              onChange={checked => editSetup({ maxEnabled: checked })}
            />

            {setupForm.maxEnabled && (
              <AmountField
                id="setup-max"
                label={maxTerm.name}
                termId="maximum-checkout"
                helper={t("views.budget.setupMaxHelper")}
                value={setupForm.maxText}
                onChange={v => {
                  setMaxTouched(true)
                  editSetup({ maxText: v })
                }}
                invalid={setupMaxShowError}
                errorText={t("views.budget.invalidAmount")}
                autoFocus
              />
            )}

            {setupCheck.nothingChosen && (
              <p id="setup-nothing-chosen" role="alert" className="text-xs text-destructive">
                {t("views.budget.setupNothingChosen")}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                className="min-h-11"
                disabled={!setupCheck.valid || setupSaveState === "saving"}
                aria-describedby={setupCheck.nothingChosen ? "setup-nothing-chosen" : undefined}
                onClick={() => void handleSetupSave()}
              >
                {setupSaveState === "saving" ? t("onboarding.shell.saving") : t("views.budget.setupSave")}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="min-h-11"
                onClick={() => dispatch({ type: "DECIDE_LATER" })}
              >
                {t("views.budget.legacyLater")}
              </Button>
              {setupSaveState === "error" && (
                <span className="text-sm text-destructive">{t("moreProfile.profile.saveError")}</span>
              )}
            </div>
          </div>
        )}

        {state === "unclassified_collapsed" && (
          <div
            role="status"
            data-testid="budget-pending-notice"
            className="flex flex-wrap items-center justify-between gap-3 rounded-bb-lg border border-gold/50 bg-gold/10 px-4 py-3"
          >
            <div className="flex flex-col gap-0.5">
              <p className="text-sm font-semibold text-ink">
                {t("views.budget.pendingNoticeTitle", { amount: legacyAmountText })}
              </p>
              <p className="text-xs text-ink-2">{t("views.budget.pendingNoticeBody")}</p>
            </div>
            <Button
              type="button"
              size="sm"
              className="min-h-9"
              onClick={() => {
                dispatch({ type: "SET_UP" })
                setFocusSetup(true)
              }}
            >
              {t("views.budget.pendingNoticeCta")}
            </Button>
          </div>
        )}

        {legacy && legacyAmount !== null && (
          <div className="flex flex-col gap-1" data-testid="previous-budget-row">
            <p className="flex flex-wrap items-center gap-1 text-sm font-medium text-ink">
              <span>{t("views.budget.previousBudgetLabel", { amount: legacyAmountText })}</span>
              <InfoButton termId="maximum-checkout" />
            </p>
            <p className="text-xs text-ink-2">
              {t("views.budget.previousBudgetHint", { amount: legacyAmountText })}
            </p>
          </div>
        )}

        {!legacy && (
          <>
            {justMigrated && (
              <p role="status" className="text-sm text-green" data-testid="budget-setup-saved">
                {t("views.budget.setupSaved")}
              </p>
            )}

            <AmountField
              id="budget-goal"
              ref={goalInputRef}
              label={goalTerm.name}
              termId="weekly-spending-goal"
              helper={goalTerm.shortDefinition}
              suffix={t("views.budget.perWeek")}
              value={goalText}
              onChange={v => {
                setGoalText(v)
                setSaveState("idle")
              }}
              invalid={showErrors && goalInvalid}
              errorText={t("views.budget.invalidAmount")}
              emptyState={
                goalText === "" && budget.weeklyGoalEurCents === null ? t("views.budget.noGoalSet") : undefined
              }
              onRemove={budget.weeklyGoalEurCents !== null ? () => void handleRemove("goal") : undefined}
              removeLabel={t("views.budget.remove")}
            />

            <AmountField
              id="budget-max"
              label={maxTerm.name}
              termId="maximum-checkout"
              optionalLabel={t("views.budget.optional")}
              helper={maxTerm.shortDefinition}
              value={maxText}
              onChange={v => {
                setMaxText(v)
                setSaveState("idle")
              }}
              invalid={showErrors && maxInvalid}
              errorText={t("views.budget.invalidAmount")}
              emptyState={
                maxText === "" && budget.maxCheckoutEurCents === null ? t("views.budget.noMaxSet") : undefined
              }
              onRemove={budget.maxCheckoutEurCents !== null ? () => void handleRemove("max") : undefined}
              removeLabel={t("views.budget.remove")}
            />

            <div className="flex items-center gap-3">
              <Button
                type="button"
                className="min-h-11"
                disabled={saveState === "saving"}
                onClick={() => void handleSave()}
              >
                {saveState === "saving" ? t("onboarding.shell.saving") : t("moreProfile.profile.saveButton")}
              </Button>
              {saveState === "saved" && (
                <span role="status" className="text-sm text-green">
                  {t("views.budget.saved")}
                </span>
              )}
              {saveState === "error" && (
                <span className="text-sm text-destructive">{t("moreProfile.profile.saveError")}</span>
              )}
            </div>
          </>
        )}
      </section>

      <BudgetOutlook refreshKey={outlookRefresh} />
    </>
  )
}

function CheckboxRow({
  id,
  label,
  checked,
  onChange,
}: {
  id: string
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <div className="flex items-center gap-3">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        className="size-5 shrink-0 rounded border-line text-green focus-visible:ring-2 focus-visible:ring-green"
      />
      <label htmlFor={id} className="text-sm text-ink">
        {label}
      </label>
    </div>
  )
}

interface AmountFieldProps {
  id: string
  label: string
  termId: GlossaryTermId
  optionalLabel?: string
  helper?: string
  suffix?: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  invalid: boolean
  errorText: string
  emptyState?: string
  onRemove?: () => void
  removeLabel?: string
  autoFocus?: boolean
}

const AmountField = forwardRef<HTMLInputElement, AmountFieldProps>(function AmountField(
  {
    id,
    label,
    termId,
    optionalLabel,
    helper,
    suffix,
    value,
    onChange,
    disabled,
    invalid,
    errorText,
    emptyState,
    onRemove,
    removeLabel,
    autoFocus,
  },
  ref
) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-1">
        <label htmlFor={id} className="text-sm font-medium text-ink">
          {label}
        </label>
        <InfoButton termId={termId} />
        {optionalLabel && (
          <span className="rounded-bb-full border border-line bg-cream px-2 py-0.5 text-xs text-ink-2">
            {optionalLabel}
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span aria-hidden="true" className="text-sm text-ink-2">
          €
        </span>
        <Input
          id={id}
          ref={ref}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          autoFocus={autoFocus}
          className="w-32"
          value={value}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? `${id}-error` : helper ? `${id}-helper` : undefined}
          onChange={e => onChange(e.target.value)}
        />
        {suffix && <span className="text-sm text-ink-2">{suffix}</span>}
        {onRemove && removeLabel && (
          <Button type="button" variant="ghost" size="sm" className="min-h-9" onClick={onRemove}>
            {removeLabel}
          </Button>
        )}
      </div>
      {emptyState && <p className="text-xs text-ink-2">{emptyState}</p>}
      {invalid && (
        <p id={`${id}-error`} role="alert" className="text-xs text-destructive">
          {errorText}
        </p>
      )}
      {helper && (
        <p id={`${id}-helper`} className="text-xs text-ink-2">
          {helper}
        </p>
      )}
    </div>
  )
})
